import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  User, Moon, Sun, Shield, Loader2, Church, Upload, Camera, MapPin, Phone, Mail, Clock, Globe
} from 'lucide-react';
import { toast } from 'sonner';

export const ImpostazioniPage = () => {
  const { user, API, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [chiesaLoading, setChiesaLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [chiesa, setChiesa] = useState(null);
  const [chiesaForm, setChiesaForm] = useState({});
  const [profileData, setProfileData] = useState({
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    telefono: user?.telefono || ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({ nome: user.nome, cognome: user.cognome, telefono: user.telefono || '' });
      if (user.chiesa_id && isAdmin()) fetchChiesa();
    }
  }, [user]);

  const fetchChiesa = async () => {
    try {
      const res = await axios.get(`${API}/chiese/mia`);
      setChiesa(res.data);
      setChiesaForm({
        nome: res.data.nome || '',
        indirizzo: res.data.indirizzo || '',
        citta: res.data.citta || '',
        referente: res.data.referente || '',
        telefono: res.data.telefono || '',
        email: res.data.email || '',
        orari_culto: res.data.orari_culto || '',
        descrizione: res.data.descrizione || '',
        sito_web: res.data.sito_web || ''
      });
    } catch (err) {
      // Church not found is ok
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/users/${user.id}`, profileData);
      toast.success('Profilo aggiornato');
    } catch (err) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChiesa = async (e) => {
    e.preventDefault();
    setChiesaLoading(true);
    try {
      await axios.put(`${API}/chiese/${chiesa.id}`, chiesaForm);
      toast.success('Informazioni chiesa aggiornate');
      fetchChiesa();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore durante il salvataggio'));
    } finally {
      setChiesaLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/chiese/${chiesa.id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo aggiornato');
      fetchChiesa();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nel caricamento del logo'));
    } finally {
      setLogoLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-2xl" data-testid="impostazioni-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-bold">Impostazioni</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Gestisci il tuo profilo e le impostazioni della chiesa</p>
      </div>

      {/* Chiesa Settings - Only for Admin */}
      {isAdmin() && chiesa && (
        <Card className="border-primary/20">
          <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Church className="w-5 h-5 text-primary" />
              La Mia Chiesa
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Modifica le informazioni della tua chiesa</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
            {/* Logo Section */}
            <div className="flex items-center gap-4 mb-6 p-3 sm:p-4 bg-muted/50 rounded-lg">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-background border-2 border-dashed border-border flex items-center justify-center overflow-hidden flex-shrink-0">
                {chiesa.logo_url ? (
                  <img src={`${API}${chiesa.logo_url.replace('/api', '')}`} alt="Logo" className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Church className="w-8 h-8 text-muted-foreground/40" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Logo della chiesa</p>
                <p className="text-xs text-muted-foreground mb-2">JPG, PNG o WebP. Consigliato 200x200px.</p>
                <div className="flex gap-2">
                  <Input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-input" data-testid="logo-upload-input" />
                  <label htmlFor="logo-input" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-input bg-background cursor-pointer hover:bg-accent transition-colors text-xs font-medium">
                    {logoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Carica Logo
                  </label>
                  <Input type="file" accept="image/*" capture="environment" onChange={handleLogoUpload} className="hidden" id="logo-camera" />
                  <label htmlFor="logo-camera" className="sm:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-input bg-background cursor-pointer hover:bg-accent transition-colors text-xs font-medium">
                    <Camera className="w-3.5 h-3.5" />
                    Foto
                  </label>
                </div>
              </div>
            </div>

            {/* Chiesa Info Form */}
            <form onSubmit={handleSaveChiesa} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Church className="w-3.5 h-3.5" />Nome Chiesa *</Label>
                <Input value={chiesaForm.nome} onChange={(e) => setChiesaForm({ ...chiesaForm, nome: e.target.value })} required data-testid="chiesa-nome-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Indirizzo</Label>
                  <Input value={chiesaForm.indirizzo} onChange={(e) => setChiesaForm({ ...chiesaForm, indirizzo: e.target.value })} data-testid="chiesa-indirizzo-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Citta</Label>
                  <Input value={chiesaForm.citta} onChange={(e) => setChiesaForm({ ...chiesaForm, citta: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Referente / Pastore</Label>
                  <Input value={chiesaForm.referente} onChange={(e) => setChiesaForm({ ...chiesaForm, referente: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />Telefono Chiesa</Label>
                  <Input value={chiesaForm.telefono} onChange={(e) => setChiesaForm({ ...chiesaForm, telefono: e.target.value })} type="tel" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Email Chiesa</Label>
                  <Input value={chiesaForm.email} onChange={(e) => setChiesaForm({ ...chiesaForm, email: e.target.value })} type="email" data-testid="chiesa-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />Sito Web</Label>
                  <Input value={chiesaForm.sito_web} onChange={(e) => setChiesaForm({ ...chiesaForm, sito_web: e.target.value })} placeholder="https://..." />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Orari di Culto</Label>
                <Textarea
                  value={chiesaForm.orari_culto}
                  onChange={(e) => setChiesaForm({ ...chiesaForm, orari_culto: e.target.value })}
                  placeholder="Es: Domenica 10:00-12:00, Mercoledi 19:00-20:30"
                  rows={2}
                  data-testid="chiesa-orari-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Descrizione</Label>
                <Textarea
                  value={chiesaForm.descrizione}
                  onChange={(e) => setChiesaForm({ ...chiesaForm, descrizione: e.target.value })}
                  placeholder="Breve descrizione della chiesa..."
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={chiesaLoading} className="w-full sm:w-auto" data-testid="save-chiesa-btn">
                {chiesaLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Salva Informazioni Chiesa
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Profile Settings */}
      <Card>
        <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <User className="w-5 h-5" />
            Profilo Personale
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">Aggiorna le tue informazioni</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input value={profileData.nome} onChange={(e) => setProfileData({ ...profileData, nome: e.target.value })} data-testid="profile-nome-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Cognome</Label>
                <Input value={profileData.cognome} onChange={(e) => setProfileData({ ...profileData, cognome: e.target.value })} data-testid="profile-cognome-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled />
              <p className="text-[11px] text-muted-foreground">L'email non puo essere modificata</p>
            </div>
            <div className="space-y-1.5">
              <Label>Telefono</Label>
              <Input value={profileData.telefono} onChange={(e) => setProfileData({ ...profileData, telefono: e.target.value })} data-testid="profile-telefono-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="save-profile-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salva Profilo
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Appearance Settings */}
      <Card>
        <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            {theme === 'light' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            Aspetto
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Tema Scuro</p>
              <p className="text-xs text-muted-foreground">Riduci l'affaticamento degli occhi</p>
            </div>
            <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="theme-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Shield className="w-5 h-5" />
            Account
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Ruolo</p>
            <p className="text-sm font-medium capitalize">{user?.ruolo?.replace('_', ' ')}</p>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Stato</p>
            <p className="text-sm font-medium capitalize">{user?.stato || 'Attivo'}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImpostazioniPage;
